/**
 * `DocumentDetailsPane` — the A-14 always-shown `Detalhes` pane.
 *
 * Operator directive (A-14, ratified 2026-08-15): search-result documents are
 * not user-editable, so their PROVENANCE (who searched what, which provider
 * served it) and the lawyer's own NOTES/RECTIFICATIONS live beside the
 * content, always shown as details in the viewer pane. Ruling:
 * `ai-docs/a14-search-doc-sidecar-ruling-miniplan.md` (split storage, RPC-only
 * writes); render plan: `ai-docs/a14-details-pane-render-miniplan.md`.
 *
 * ## The invariants this component carries
 *
 * - **Parent-keyed everything.** One `case.document_details_read` call
 *   delivers provenance + notes (the R6 pin); the note save posts the PARENT
 *   path. This file must never mention the notes-sidecar suffix — the
 *   SPA-wide T9b grep pin reddens if anyone types it.
 * - **Honest absence, both directions.** `provenance: null` (nothing
 *   recorded — normal for a non-search doc) renders NO provenance section and
 *   no placeholder; `{entries: []}` (recorded, empty) renders the section
 *   header with an explicit empty wording — the two states are
 *   distinguishable on screen because they are distinguishable in the
 *   contract. A FAILED read renders an error line, never "no details".
 * - **Provider ids through `providerDisplayLabel`** — the SAME helper the
 *   A-11 TrailRow badge uses, so the pane and the badge cannot drift on one
 *   vocabulary. Unknown ids render verbatim.
 * - **A failed save is not a silent success.** The RPC error renders inline
 *   and the editor KEEPS the unsaved content — a note is irreplaceable, and
 *   rendering it as saved when it is not is the worst honesty failure this
 *   surface can produce.
 * - **No autosave.** Every write stamps authorship and versions kernel-side;
 *   keystroke-autosave would spam both. Explicit save button only.
 *
 * Mount pattern: fixed at the bottom of `WorkspaceFilePane`, the
 * `SourceAssessmentPane` precedent — sibling panes for sibling tiers
 * (`sources/` ⇒ assessment; Generated roots ⇒ details). Gates are disjoint by
 * root, so the two can never stack.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { useGatewayStore } from "@/stores/useGatewayStore";
import { useActiveContextStore, useEffectiveScope } from "@/stores/useActiveContextStore";
import { useDocumentDetails } from "@/hooks/useDocumentDetails";
import { isGeneratedZonePath } from "@/lib/workspace/case-files-view";
import { providerDisplayLabel } from "@/lib/chat/tool-activity";
import type { FileRef } from "@/stores/useWorkspaceFileStore";
import type { DocumentProvenanceEntry } from "@/types/gateway";
import { cn } from "@/lib/utils";

interface DocumentDetailsPaneProps {
  file: FileRef;
}

export function DocumentDetailsPane({ file }: DocumentDetailsPaneProps) {
  const { t } = useTranslation();
  const scope = useEffectiveScope();
  const rpc = useGatewayStore((s) => s.rpc);
  const caseId = useActiveContextStore((s) => s.selection?.caseId ?? null);

  const eligible = scope !== "org" && isGeneratedZonePath(file.path);
  const { details, isPending, isError, refetch } = useDocumentDetails(
    eligible ? file.path : null,
  );

  // ── notes editor state ──────────────────────────────────────────────────
  // `draft === null` ⇒ tracking the server value (render `details.notes`);
  // a string ⇒ the lawyer has touched the editor and their bytes win until a
  // successful save. Keyed off the file path so navigating away and back
  // never carries a stale draft across documents.
  const [draft, setDraft] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "error">("idle");
  useEffect(() => {
    setDraft(null);
    setSaveState("idle");
  }, [file.path]);

  if (!eligible) return null;

  const notesValue = draft ?? details?.notes ?? "";
  const dirty = draft !== null && draft !== (details?.notes ?? "");

  async function handleSave() {
    if (!rpc || !caseId || draft === null) return;
    setSaveState("saving");
    try {
      await rpc.caseDocumentNoteWrite({ caseId, parentPath: file.path, content: draft });
      setSaveState("idle");
      setDraft(null); // server value is authoritative again
      refetch(); // re-read so `notesAuthoredBy` renders from a MEASURED value
    } catch {
      // ⚠ The unsaved content stays in `draft` — never cleared on failure.
      setSaveState("error");
    }
  }

  return (
    <aside
      data-testid="document-details-pane"
      aria-label={t("workspace.detailsPane.title")}
      className="max-h-[40%] shrink-0 overflow-y-auto border-t border-cream-300 bg-cream-50 px-3 py-2 text-caption dark:border-slate-700 dark:bg-slate-900"
    >
      <div className="mb-1 select-none font-medium text-coffee-800 dark:text-cream-200">
        {t("workspace.detailsPane.title")}
      </div>

      {isPending && (
        <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
          <Loader2 size={12} className="animate-spin" />
          <span>{t("workspace.detailsPane.loading")}</span>
        </div>
      )}

      {/* A failed READ is an error, never "no details" — the contract throws
          instead of returning the absence shape, and the render honors that. */}
      {isError && (
        <div data-testid="details-read-error" className="text-red-600 dark:text-red-400">
          {t("workspace.detailsPane.readError")}
        </div>
      )}

      {details && (
        <>
          {/* provenance — ONLY when recorded (null ⇒ nothing, not a
              placeholder). An empty entries array is a DIFFERENT state and
              says so explicitly. */}
          {details.provenance && (
            <section data-testid="details-provenance" className="mb-2">
              <div className="select-none text-coffee-600 dark:text-cream-500">
                {t("workspace.detailsPane.provenanceTitle")}
              </div>
              {details.provenance.entries.length === 0 ? (
                <div className="text-slate-500 dark:text-slate-400">
                  {t("workspace.detailsPane.provenanceEmpty")}
                </div>
              ) : (
                <ul className="mt-0.5 flex flex-col gap-0.5">
                  {details.provenance.entries.map((entry, i) => (
                    <ProvenanceRow key={i} entry={entry} />
                  ))}
                </ul>
              )}
            </section>
          )}

          {/* notes — always present for an eligible doc (the editor IS the
              affordance; an absent note is an empty editor, indistinguishable
              from an empty note by design). */}
          <section data-testid="details-notes">
            <label
              htmlFor="details-notes-editor"
              className="select-none text-coffee-600 dark:text-cream-500"
            >
              {t("workspace.detailsPane.notesTitle")}
            </label>
            {details.notesAuthoredBy === "user" && (
              <span
                data-testid="details-notes-authored"
                className="ml-2 select-none text-cream-500 dark:text-cream-500"
              >
                {t("workspace.detailsPane.notesAuthoredUser")}
              </span>
            )}
            <textarea
              id="details-notes-editor"
              data-testid="details-notes-editor"
              value={notesValue}
              onChange={(e) => setDraft(e.target.value)}
              disabled={saveState === "saving"}
              rows={3}
              placeholder={t("workspace.detailsPane.notesPlaceholder")}
              className="mt-1 w-full resize-y rounded border border-cream-300 bg-white p-1.5 text-caption text-coffee-900 dark:border-slate-700 dark:bg-slate-950 dark:text-cream-200"
            />
            <div className="mt-1 flex items-center gap-2">
              <button
                type="button"
                data-testid="details-notes-save"
                onClick={handleSave}
                disabled={!dirty || saveState === "saving" || !rpc || !caseId}
                className={cn(
                  "rounded border border-cream-400 px-2 py-0.5 text-caption font-medium text-coffee-800 hover:bg-cream-100 disabled:opacity-50 dark:border-slate-600 dark:text-cream-200 dark:hover:bg-slate-800",
                )}
              >
                {saveState === "saving"
                  ? t("workspace.detailsPane.saving")
                  : t("workspace.detailsPane.save")}
              </button>
              {saveState === "error" && (
                <span data-testid="details-notes-save-error" className="text-red-600 dark:text-red-400">
                  {t("workspace.detailsPane.saveError")}
                </span>
              )}
            </div>
          </section>
        </>
      )}
    </aside>
  );
}

/** One provenance row: what was searched / fetched, and WHO served it — the
 *  provider through the SAME label helper as the A-11 badge (one vocabulary).
 *  The `"http"` sentinel renders the direct-access wording, never raw. */
function ProvenanceRow({ entry }: { entry: DocumentProvenanceEntry }) {
  const { t } = useTranslation();
  const what = entry.query ?? entry.url ?? entry.path;
  return (
    <li
      data-testid="details-provenance-row"
      data-provider={entry.provider}
      className="flex min-w-0 items-baseline gap-1.5 text-coffee-600 dark:text-cream-400"
    >
      <span className="min-w-0 truncate" title={what}>
        {what}
      </span>
      <span className="shrink-0 select-none text-cream-500 dark:text-cream-500">
        {entry.provider === "http"
          ? t("chat.activity.provider.direct")
          : t("chat.activity.provider.via", {
              provider: providerDisplayLabel(t, entry.provider),
            })}
      </span>
    </li>
  );
}
